/**
 * placeDigits — ADR-022 §4: **turn NAMED TIPS into placed joints.** Hand Studio
 * E3a, second generation.
 *
 * THE INPUT IS A PIN, NOT AN ANALYSIS. Until 2026-08-11 this took one
 * `SeparationResult` and a `branch-N → digit` map, which bundled every tip
 * inside a single voxel analysis and therefore required all five digits to
 * separate at one resolution — the field failure ADR-022 §1 records (the
 * resolution hunt, and the dead end of deleting correct answers until the rest
 * co-separate). The constraint was never mathematical. Measured: a FINGER is
 * constructed from four things — the wrist-cut centroid, the TIP, the
 * `palmFraction` prior, and the φ splits — and the analyzer's branch chain
 * contributes nothing to it (it is a tip fragment: 2–41% of the digit, 2–14%
 * on the tribal warrior). So a finger needs only `(tip, name)`, and tips from
 * DIFFERENT resolutions may sit in one hand.
 *
 * THE THUMB IS THE ONE TRUE EXCEPTION and stays one: its chain genuinely covers
 * 34–57% of the digit and its low split IS the CMC, so when a chain is supplied
 * the thumb is placed along it at φ fractions. Without one it falls back to the
 * same wrist→tip span the fingers use and says so (`thumb-span-fallback`) —
 * Allen's D2 ruling, closing the span-vs-chain question left open 2026-07-27: a
 * thumb placed by prior with a loud marker beats refusing an otherwise good hand.
 *
 * Placement (HAND_STUDIO_KNUCKLES.md, amended by ADR-024): all finger MCPs
 * share ONE knuckle radius from the wrist (the row is an arc at a common
 * distance — Allen's row-alignment prior), taken from the longest finger's
 * span; PIP/DIP then split MCP→tip at golden-ratio fractions (segments
 * φ²:φ:1 ⇒ [0, 0.5, 0.809, 1]).
 *
 * THE CHORD IS THE DEFAULT AND THE TUBE IS THE EXCEPTION (ADR-024 §2.1, D1).
 * T2 walked the medial path for every digit, always, and the field said half
 * right (ADR-023 §12): seating won, structure lost — the path's first half runs
 * through the palm every digit shares, so five knuckles collapsed onto its
 * medial axis and routes leaked into neighbouring fingers. But on a straight
 * digit the chord and the path are the same line, so the tube buys nothing
 * there. Every digit's tube is still WALKED (it is the measurement); placement
 * takes the chord unless the chord LEAVES THE FLESH (`chordEscapeVoxels` — the
 * ruled length-ratio metric could not separate lattice meander from a real
 * curl; see the option's doc for the corpus numbers). Per digit, switched not
 * blended (D1), the gate a knob with provenance (§11).
 *
 * WHAT THIS NO LONGER DOES. Naming moved out (`propose-digit-names`, demoted to
 * a pre-fill a person confirms), and with it the anomalous-count refusal — a
 * sixth candidate is simply never pinned. The §11 declaration-contradiction
 * refusal is gone too, because there is no declaration here to contradict: the
 * pins ARE the claim, and ADR-022 §3 makes the measurement their witness rather
 * than their gate. What remains fail-loud is anything that would place geometry
 * nobody asked for: a duplicate digit name, an empty pin set, a non-finite
 * wrist, a `palmFraction` outside (0,1).
 *
 * Everything is VOXEL-INDEX space; the world↔voxel crossing has exactly one
 * owner (`voxelPointToWorld`/`worldPointToVoxel`, ADR-022 §5.1) and it is not
 * here. Depends on: hands/voxel-grid (VoxelPoint), hands/voxel-math,
 * hands/propose-digit-names (the digit vocabulary), hands/separation-profile
 * (EvidenceMarker). Depended on by: E3b frame authoring + E3c graft, kernel index.
 */
import { KernelError } from '../ir/errors.js';
import { distanceToOccupied, type VolumeGrid, type VoxelPoint } from './voxel-grid.js';
import type { BranchChain, EvidenceMarker } from './separation-profile.js';
import { digitAnatomy, type DigitJoint } from './digit-anatomy.js';
import { classifyHand, type HandClassReport } from './hand-class.js';
import { medialPath, type MedialPath } from './medial-path.js';
import { DIGIT_ORDER, type DigitName } from './propose-digit-names.js';
import { add, dist, dominantAxis, dot, len, lerp, scale, sub } from './voxel-math.js';

export type { DigitName } from './propose-digit-names.js';
export { DIGIT_ORDER } from './propose-digit-names.js';

// JOINT_FRACTIONS is GONE (ADR-023 §5). It was a second copy of the finger
// ladder living next to the placement that applied it; the one copy now lives
// in `digit-anatomy`, where the thumb's different ladder sits beside it and the
// two can be told apart. Placement asks what a digit IS before building it.
/**
 * Anthropometric prior: the knuckle row sits ~50% of wrist→longest-fingertip.
 * Was 0.55; tuned to 0.50 on Allen's field read (2026-07-28, fresh anime
 * specimen): each MCP belongs just PROXIMAL of the webbing, on the wrist side,
 * and at 0.55 the whole row landed just beyond it. A prior, not a law (§11 knob).
 */
const PALM_FRACTION_DEFAULT = 0.5;
/** Branch-fragment axis vs wrist→tip span divergence worth surfacing (degrees). */
const AXIS_DIVERGENCE_DEGREES = 30;

/** A cannot-place condition — a producer/caller bug, fail-loud (ADR-010 §7). */
export class DigitPlacementError extends KernelError {
  constructor(readonly issues: readonly string[]) {
    super(`Cannot place digits (${issues.length} issue(s)):\n` + issues.map((i) => `  - ${i}`).join('\n'));
  }
}

/**
 * ONE DIGIT'S TARGET: a named tip a human vouched for (ADR-022 §3).
 *
 * `tip` is the whole requirement for a finger. `chain` is optional evidence the
 * THUMB uses and every finger ignores — supply it when some resolution
 * separated this digit and you have its medial path; omit it freely, including
 * for a digit no resolution ever separated (a pin on a fused mitten, or Bart's
 * sixth finger). `sourceId` is provenance only — a branch id, a consensus
 * cluster id, or a user-pin id — and is NEVER a contract key downstream.
 */
export interface DigitPin {
  readonly digit: DigitName;
  readonly tip: VoxelPoint;
  /**
   * ADR-024 §2.2: an OPTIONAL second row target — the first knuckle, clicked
   * the same way the tip was. A pinned knuckle replaces the row inference the
   * same way a tip pin replaced the naming inference: the digit routes
   * MCP→tip, entirely inside its own tube, so the shared-palm corridor (and
   * the leak it caused, ADR-023 §12.1) is structurally out of the picture for
   * this digit. Per D2 the click decides the STATION exactly; the volume
   * decides where inside it (the cross-section centroid). On a thumb this pins
   * the MCP — the CMC stays at the wrist (D2, ADR-023).
   *
   * Omit freely: an unpinned digit routes wrist→tip, exactly as before — the
   * feature has no floor to fall through, and the cost lands only on the
   * digits that have the problem.
   */
  readonly knuckle?: VoxelPoint;
  readonly chain?: readonly VoxelPoint[];
  readonly sourceId?: string;
}

/**
 * Build pins from ONE analysis — the adapter that lets a single-resolution
 * `SeparationResult` feed the tip-driven door.
 *
 * This is deliberately the *narrow* case of what a pin can express: every tip
 * comes from the same analysis, which is precisely the limitation ADR-022
 * removes. It exists so the Document path and the fixture corpus can cross to
 * the new API in one step (T1) while the pins they carry are still branch-shaped,
 * and so the equivalence probe has something to compare against. T2 replaces its
 * callers with real cross-resolution pins; nothing here needs to change for that,
 * because a pin does not care where its tip came from.
 *
 * Ids that match no branch and digits claimed twice are the CALLER's bug and
 * fail loud, exactly as the old explicit path did. Branches with no assignment
 * are not an oversight — they are the statement "that is not a digit".
 */
export function pinsFromBranches(
  branches: readonly BranchChain[],
  assignment: Readonly<Record<string, DigitName>>,
): DigitPin[] {
  const issues: string[] = [];
  const entries = Object.entries(assignment);
  if (entries.length === 0) {
    issues.push('assignment is empty — say nothing by omitting the call, not by passing {}');
  }
  const pins: DigitPin[] = [];
  for (const [branchId, digit] of entries) {
    const branch = branches.find((b) => b.id === branchId);
    if (branch === undefined) {
      issues.push(
        `assignment names branch '${branchId}', which this hand does not have ` +
          `(branches: ${branches.map((b) => b.id).join(', ') || 'none'})`,
      );
      continue;
    }
    if (branch.chain.length < 2) {
      issues.push(`branch '${branchId}' has a ${branch.chain.length}-point chain — no tip to pin`);
      continue;
    }
    pins.push({
      digit,
      tip: branch.chain[branch.chain.length - 1]!,
      chain: branch.chain,
      sourceId: branchId,
    });
  }
  if (issues.length > 0) throw new DigitPlacementError(issues);
  return pins;
}

/** One placed joint, in voxel-index space. */
export interface PlacedJoint {
  /** `mcp`/`pip`/`dip` are the articulating joints; `tip` is the leaf marker. */
  /**
   * What this joint IS, from `digit-anatomy` — `mcp/pip/dip` on a finger,
   * `cmc/mcp/ip` on a thumb. Typed as the shared union so a consumer cannot
   * quietly assume finger semantics (ADR-023 §6).
   */
  readonly joint: DigitJoint;
  readonly position: VoxelPoint;
  /** Arclength fraction base→tip this joint sits at (0, ~0.5, ~0.809, 1). */
  readonly arclengthFraction: number;
}

/** One named, placed digit + the BBSA bone ids its joints graft to (§6). */
export interface PlacedDigit {
  readonly name: DigitName;
  /** Where this tip came from — evidence only, never a key (ADR-178's lesson). */
  readonly sourceId: string | null;
  /** BBSA ontology ids for this side: bone1=MCP … bone4=tip (Mixamo-named). */
  readonly boneIds: readonly [string, string, string, string];
  /** MCP, PIP, DIP, tip — in that order. */
  readonly joints: readonly PlacedJoint[];
  readonly lengthVoxels: number;
  /**
   * How these positions were arrived at. THE POSITIONS ARE ALWAYS THE CHORD
   * (Allen's field ruling, 2026-08-14 evening: the tube's interim points made
   * "swollen, deformed, elongated fingers"; the straight biometric line looks
   * right) — what varies is the volume's TESTIMONY about it.
   *
   * `chord-validated` — the tube was walked and the chord fits the flesh
   * (escape within the gate). The normal outcome.
   * `phi-prior` — nobody could endorse it: the tube could not be walked at
   * all (§7: a detached pin, a mitten at this resolution), or the chord
   * measurably leaves the flesh (`digit-chord-escapes` says how far). The
   * positions are the same construction either way.
   *
   * A union rather than a single literal on purpose: a consumer that wants to
   * trust a position has to say which claim it can accept.
   */
  readonly provenance: 'phi-prior' | 'chord-validated';
  /**
   * ADR-024's switch measurement, kept as evidence: how much longer this
   * digit's medial path is than its knuckle→tip chord (1 = straight; the
   * thumb measures CMC→tip). `null` when no tube could be walked — the one
   * case where nobody measured anything. Consumed by the hand classifier
   * (ADR-024 §2.4) and the graft report; never a gate.
   */
  readonly curl: number | null;
}

/** Row colinearity residuals (mean joint→best-fit-line distance, voxels). */
export interface RowResiduals {
  readonly mcp: number;
  readonly pip: number;
  readonly dip: number;
}

export interface PlacedHand {
  readonly side: 'left' | 'right';
  readonly digits: readonly PlacedDigit[];
  /** How colinear the four finger knuckle rows are — the §3.2 diagnostic. */
  readonly rowResiduals: RowResiduals;
  /**
   * Which region of the curl × adduction plane this hand measured into
   * (ADR-024 §2.4) — evidence for the graft report and the surface's
   * propose-the-knuckle-row decision (D3). Derived, never declared.
   */
  readonly handClass: HandClassReport;
  readonly markers: readonly EvidenceMarker[];
}

export interface PlaceDigitsOptions {
  readonly side: 'left' | 'right';
  /**
   * The wrist cut-plane centroid in VOXEL-INDEX space — the construction's
   * origin. NOT the host hand bone: on some generated rigs that bone sits
   * forward of the palm (measured variance — HAND_STUDIO_KNUCKLES.md), while
   * the wrist cut is where the hand was actually severed from the arm.
   */
  readonly wristCentre: VoxelPoint;
  /**
   * **The volume the digits are being placed INTO** (ADR-023 §4, required).
   *
   * Placement without it produced positions it could not check: interior joints
   * went on the straight chord from knuckle to tip, on the stated premise that
   * "rest-pose fingers are straight" — false for the entire relaxed-hand class,
   * and measured at up to ELEVEN voxels outside the flesh (§1.2). The grid is
   * what lets a proportion become a *place*.
   *
   * Injected rather than reached for, and required rather than optional,
   * because an optional grid is the coexistence §5 deletes: a caller that
   * forgot it would silently get the old chord placement back.
   */
  readonly grid: VolumeGrid;
  /**
   * Where the knuckle row sits along wrist→longest-fingertip, in (0,1)
   * (default 0.50). A prior, not a law: creature palms vary, so it is a §11
   * knob and every finger MCP is `phi-prior` provenance — correctable.
   */
  readonly palmFraction?: number;
  /**
   * Soft knuckle-row regularization blend in [0,1] (default 0 = report only).
   * >0 nudges each finger joint toward its row's best-fit line; residuals are
   * always reported regardless.
   */
  readonly rowRegularization?: number;
  /**
   * Where the grafted chain will actually HANG — the hand bone's own origin,
   * in voxel space (field, 2026-08-14, Eric): the thumb's CMC used to seat at
   * the wrist-CUT centre, while the chain parents to the hand JOINT, which on
   * generated rigs sits mid-hand. The result was two bones to the knuckle — a
   * connector pointing backward out of the hand, then a metacarpal doubling
   * back across the entire palm — and that palm-sweeping segment seeded its
   * weights through the middle finger's territory (Allen: "overinfluences the
   * middle finger"). With the attach supplied, the thumb's CMC seats HERE:
   * Thumb1 becomes one clean bone from the wrist to the knuckle, and its
   * segment runs the radial edge instead of the palm. Optional; omitted, the
   * wrist cut centre stands in (they coincide after a wrist recenter).
   */
  readonly handAttach?: VoxelPoint;
  /**
   * ADR-024 D1's switch, AS AMENDED BY THE CORPUS RUN the ADR itself ordered:
   * how far the chord construction may stray from occupied volume, in voxels,
   * before placement spends the tube (default 1 — the quantization floor is
   * 0.87, ADR-023 §1.2, so 1 is the tightest honest gate).
   *
   * The ruled metric was `curl = path.length / chord` with a 1.05 threshold.
   * Measured across the committed corpus (2026-08-14, res 48, smoothed), the
   * length ratio DOES NOT SEPARATE the classes: the extended goblin measures
   * 1.02–1.13 while gilda's genuinely curled pinky measures 1.08 — lattice
   * meander inflates a straight digit's path length by as much as a real curl
   * does. What separates perfectly is the decision-relevant quantity itself:
   * whether the chord LEAVES THE FLESH (koda's bad chord: 5.1 voxels out;
   * every valid chord: under ~1). That is also Allen's sentence, read
   * literally — "a straight line … unless the tube is bent" ENOUGH THAT THE
   * LINE EXITS IT — and it makes the switch self-gating: a kept chord fits the
   * flesh by the very test that kept it. The switch is still hard, still
   * per digit (D1); only the measured quantity changed, and `curl` survives on
   * the placed digit as classifier evidence.
   */
  readonly chordEscapeVoxels?: number;
}

/** 3-point moving-average smoothing (endpoints fixed) — knocks down voxel quantization noise. */
function smoothChain(chain: readonly VoxelPoint[], passes = 2): VoxelPoint[] {
  let pts = chain.map((p) => ({ ...p }));
  for (let pass = 0; pass < passes; pass += 1) {
    pts = pts.map((p, i) =>
      i === 0 || i === pts.length - 1 ? p : scale(add(add(pts[i - 1]!, scale(p, 2)), pts[i + 1]!), 0.25),
    );
  }
  return pts;
}


/** Mean distance of points to their best-fit line (through the centroid). */
function lineResidual(points: readonly VoxelPoint[]): number {
  if (points.length < 2) return 0;
  const mean = scale(points.reduce(add, { x: 0, y: 0, z: 0 }), 1 / points.length);
  const axis = dominantAxis(points);
  let s = 0;
  for (const p of points) {
    const d = sub(p, mean);
    s += len(sub(d, scale(axis, dot(d, axis))));
  }
  return s / points.length;
}

function boneIds(side: 'left' | 'right', name: DigitName): readonly [string, string, string, string] {
  const S = side === 'left' ? 'Left' : 'Right';
  return [`${S}Hand${name}1`, `${S}Hand${name}2`, `${S}Hand${name}3`, `${S}Hand${name}4`];
}

const finite = (p: VoxelPoint | undefined): boolean =>
  p !== undefined && Number.isFinite(p.x) && Number.isFinite(p.y) && Number.isFinite(p.z);


/** ADR-024 D1 (amended): how far the chord may stray from the flesh, voxels. */
const CHORD_ESCAPE_VOXELS_DEFAULT = 1;


/**
 * The switch's measurement (ADR-024 D1, amended — see `chordEscapeVoxels`):
 * the worst distance-to-flesh over dense samples of the segment the bones
 * would actually lie on. Dense, not joints-only, because the bind seeds each
 * bone's field from the voxels its SEGMENT passes through (ADR-023 §1.3) — a
 * chord whose joints kiss the flesh while its middle crosses a gap still
 * seeds from empty space, and that is the disease being prevented.
 */
function chordEscapeOf(grid: VolumeGrid, from: VoxelPoint, to: VoxelPoint): number {
  const steps = Math.max(2, Math.ceil(dist(from, to) * 2)); // every half voxel
  let worst = 0;
  for (let i = 0; i <= steps; i += 1) {
    const d = distanceToOccupied(grid, lerp(from, to, i / steps));
    if (d > worst) worst = d;
  }
  return worst;
}

/**
 * ADR-024 §2.1's measurement: how much longer the medial path is than the
 * straight line between the same two points — FROM `baseArc` to the tip, never
 * from the wrist. The ADR's formula is `curl = path.length / |tip − knuckle|`
 * and the base matters more than it looks: the wrist half of every path runs
 * through the PALM, whose corridor meanders enough to inflate a straight
 * digit's whole-path curl past any usable threshold (measured on the goblin —
 * the extended control took the path). The digit is knuckle→tip; that is the
 * stretch whose bend is being asked about.
 *
 * 1.0 = straight (the chord IS the path); the further above, the more the
 * chord cuts an arc through empty space. Measured on the SEATED stations so
 * both numbers describe the same curve — never against the caller's raw pin,
 * which may have snapped. A degenerate remainder reads 1: nothing bends in a
 * point.
 */
function curlOf(path: MedialPath, baseArc: number): number {
  if (!(path.length > 1e-9)) return 1;
  const clamped = Math.max(0, Math.min(baseArc, path.length));
  if (!(path.length - clamped > 1e-9)) return 1;
  // The measured length is taken on a SMOOTHED copy of the remainder, because
  // the seated stations wiggle by quantization (±half a voxel each) and a
  // short remainder's summed jitter masquerades as curl — a straight digit
  // must not fail the switch for being drawn on a lattice. Same instrument as
  // `smoothChain`, same reason.
  const pts: VoxelPoint[] = [path.at(clamped / path.length)];
  for (const s of path.stations) if (s.arclength > clamped) pts.push(s.at);
  if (pts.length < 2) return 1;
  const smooth = smoothChain(pts);
  let remaining = 0;
  for (let i = 1; i < smooth.length; i += 1) remaining += dist(smooth[i - 1]!, smooth[i]!);
  const chord = dist(smooth[0]!, smooth[smooth.length - 1]!);
  return chord > 1e-9 && remaining > chord ? remaining / chord : 1;
}



/**
 * Place MCP/PIP/DIP/tip for each pinned digit. See the module header for the
 * full contract. Returns a {@link PlacedHand}; throws {@link DigitPlacementError}.
 */
export function placeDigits(pins: readonly DigitPin[], options: PlaceDigitsOptions): PlacedHand {
  const issues: string[] = [];
  if (pins.length === 0) {
    issues.push('no digit pins — placement needs at least one named tip (omit the call rather than passing [])');
  }
  const claimedBy = new Map<DigitName, string>();
  for (const pin of pins) {
    if (!DIGIT_ORDER.includes(pin.digit)) {
      issues.push(`pin names '${pin.digit}', which is not a digit`);
      continue;
    }
    const already = claimedBy.get(pin.digit);
    if (already !== undefined) {
      issues.push(`digit '${pin.digit}' is pinned twice (${already} and ${pin.sourceId ?? 'unnamed'}) — one digit, one pin`);
      continue;
    }
    claimedBy.set(pin.digit, pin.sourceId ?? 'unnamed');
    if (!finite(pin.tip)) {
      issues.push(`pin '${pin.digit}' has a non-finite tip ${JSON.stringify(pin.tip)}`);
    }
    if (pin.knuckle !== undefined && !finite(pin.knuckle)) {
      issues.push(`pin '${pin.digit}' has a non-finite knuckle ${JSON.stringify(pin.knuckle)} — omit the field rather than passing garbage`);
    }
  }
  const palmFraction = options.palmFraction ?? PALM_FRACTION_DEFAULT;
  if (!(palmFraction > 0 && palmFraction < 1)) {
    issues.push(
      `palmFraction ${palmFraction} is outside (0,1) — it is the knuckle row's fraction of wrist→longest-fingertip`,
    );
  }
  if (options.handAttach !== undefined && !finite(options.handAttach)) {
    issues.push(`handAttach ${JSON.stringify(options.handAttach)} is not a finite voxel point — omit it rather than passing garbage`);
  }
  const chordEscape = options.chordEscapeVoxels ?? CHORD_ESCAPE_VOXELS_DEFAULT;
  if (!(chordEscape >= 0)) {
    issues.push(
      `chordEscapeVoxels ${chordEscape} is negative — it is how far the chord construction may stray ` +
        `from occupied volume before the tube is spent (0 sends any measurable stray to the path)`,
    );
  }
  if (options.grid === undefined || options.grid === null) {
    issues.push(
      'no grid — placement seats joints INSIDE the volume (ADR-023), so it cannot run without the ' +
        'voxel grid the digits are being placed into. Every caller already holds one.',
    );
  }
  const wrist = options.wristCentre;
  if (!finite(wrist)) {
    issues.push(
      `wristCentre ${JSON.stringify(wrist)} is not a finite voxel point — placement lays fingers along ` +
        `wrist→tip, so it cannot run without the wrist cut centroid`,
    );
  }
  if (issues.length > 0) throw new DigitPlacementError(issues);

  const markers: EvidenceMarker[] = [];
  /** Digits whose chord the volume could not endorse — the classifier's curl axis. */
  const chordEscaped = new Set<DigitName>();
  const alpha = options.rowRegularization ?? 0;
  // Stable Thumb→Pinky order regardless of the caller's array order, so the
  // placed digits (and therefore the grafted bones) never depend on it.
  const ordered = [...pins].sort((a, b) => DIGIT_ORDER.indexOf(a.digit) - DIGIT_ORDER.indexOf(b.digit));
  if (ordered.length < DIGIT_ORDER.length) {
    markers.push({
      code: 'incomplete-digit-set',
      detail: { fingerCount: ordered.filter((p) => p.digit !== 'Thumb').length },
    });
  }

  const digitOf = (
    positions: readonly VoxelPoint[],
    pin: DigitPin,
    length: number,
    provenance: PlacedDigit['provenance'],
    curl: number | null,
    fractions?: readonly number[],
  ): PlacedDigit => ({
    name: pin.digit,
    sourceId: pin.sourceId ?? null,
    boneIds: boneIds(options.side, pin.digit),
    // THE NAMES COME FROM THE ANATOMY, not from a tuple beside the placement
    // (ADR-023 D3): a thumb's nodes are cmc/mcp/ip and a finger's are mcp/pip/dip.
    // The recorded fraction is the one the POSITIONS were laid at — the
    // restored chain thumb walks φ, not the anatomy row (§13.1).
    joints: positions.map((position, i) => ({
      joint: digitAnatomy(pin.digit).joints[i]!,
      position,
      arclengthFraction: (fractions ?? digitAnatomy(pin.digit).fractions)[i]!,
    })),
    lengthVoxels: Number(length.toFixed(4)),
    provenance,
    curl: curl === null ? null : Number(curl.toFixed(4)),
  });


  // ── THE TUBES, WALKED ONCE ────────────────────────────────────────────────
  // Every digit's medial path from the wrist to its own pinned tip. Taken up
  // front because the knuckle-row prior is a property of the SET — it needs the
  // longest finger before any single finger can place — and because a digit
  // whose tube cannot be walked has to be known before the row is measured.
  //
  // `null` is a specimen, not a failure (§7): a pin on a detached scrap, or a
  // mitten whose lobes do not connect at this resolution.
  const tubes = new Map<DigitName, MedialPath | null>();
  for (const pin of ordered) {
    const path = medialPath(options.grid, wrist, pin.tip);
    tubes.set(pin.digit, path);
    if (path === null) {
      markers.push({
        code: 'digit-medial-path-unreachable',
        detail: {
          digit: pin.digit,
          source: pin.sourceId ?? 'unnamed',
          chordVoxels: Number(dist(wrist, pin.tip).toFixed(4)),
        },
      });
    }
  }

  // THE ROW PRIOR IS A SPAN AGAIN (the field ruling, 2026-08-14 evening). The
  // arclength version existed to serve the tube walk — "knuckles at the same
  // distance TRAVELLED" — and died with it: measured along a meandering
  // low-poly tube it inflated past real spans, marked honest digits
  // finger-shorter-than-palm, and broke the equal-radius row the biometric
  // construction is built on. palmFraction of the LONGEST finger's straight
  // span, as ratified (HAND_STUDIO_KNUCKLES.md).
  const fingerLengths = ordered
    .filter((p) => p.digit !== 'Thumb')
    .map((p) => dist(wrist, p.tip))
    .filter((h) => h > 1e-9);
  const knuckleArc = fingerLengths.length > 0 ? palmFraction * Math.max(...fingerLengths) : 0;

  /**
   * A CHORD THAT ESCAPES IS REPORTED, NEVER RE-ROUTED (Allen's field ruling,
   * 2026-08-14 evening — the interior ladder is ALWAYS the chord). The tube
   * survives as an instrument (curl, class, this report); it stopped being a
   * placer the day the field showed what its interim points do on real hands:
   * "swollen, deformed, elongated fingers that bend and curve in unnatural
   * ways". D4: the report, not the surface.
   */
  const reportEscape = (pin: DigitPin, escape: number, curl: number | null): void => {
    chordEscaped.add(pin.digit);
    markers.push({
      code: 'digit-chord-escapes',
      detail: {
        digit: pin.digit,
        source: pin.sourceId ?? 'unnamed',
        escapeVoxels: Number(escape.toFixed(4)),
        gateVoxels: chordEscape,
        ...(curl === null ? {} : { curl: Number(curl.toFixed(4)) }),
      },
    });
  };

  /**
   * The knuckle-based FINGER construction (ADR-024 §2.2/§2.3): given the
   * digit's base, lay the biometric ladder on the STRAIGHT chord base→tip.
   * Reached two ways, and `mode` is the difference: a `pinned` knuckle is a
   * CLICK — a surface point — so the station is seated to its cross-section
   * centroid (D2); a `row-inferred` knuckle is derived from an already-interior
   * MCP moved along the measured row (§2.3), is used as-is, and claims no
   * authority over a tube it cannot walk (the first-pass digit is kept, and
   * nothing is said — the row is a prior, not a person).
   */
  const buildFromKnuckle = (pin: DigitPin, knuckleAt: VoxelPoint, mode: 'pinned' | 'row-inferred'): PlacedDigit | null => {
    const tip = pin.tip;
    const ladder = digitAnatomy(pin.digit).fractions;
    const kTube = medialPath(options.grid, knuckleAt, tip);
    if (kTube === null) {
      if (mode === 'row-inferred') return null;
      // §7: a knuckle click the volume cannot connect to its tip (a detached
      // scrap, a mitten at this resolution). Keep the pin AS the MCP — a
      // human's click outranks a formula — chord out, and say so.
      markers.push({
        code: 'knuckle-pin-unseated',
        detail: { digit: pin.digit, source: pin.sourceId ?? 'unnamed' },
      });
      return digitOf(ladder.map((f) => lerp(knuckleAt, tip, f)), pin, dist(knuckleAt, tip), 'phi-prior', null);
    }
    const mcp = mode === 'pinned' ? kTube.stations[0]!.at : knuckleAt;
    const curl = curlOf(kTube, 0);
    const chordPos = ladder.map((f) => lerp(mcp, tip, f));
    const escape = chordEscapeOf(options.grid, mcp, tip);
    if (escape <= chordEscape) return digitOf(chordPos, pin, dist(mcp, tip), 'chord-validated', curl);
    reportEscape(pin, escape, curl);
    return digitOf(chordPos, pin, dist(mcp, tip), 'phi-prior', curl);
  };

  const place = (pin: DigitPin): PlacedDigit | null => {
    // ── THE THUMB STARTS AT THE WRIST (ADR-023 D2 + §1.5) ────────────────────
    // Its first node is the METACARPAL, whose CMC sits at the wrist — that is
    // the digit's `baseLandmark`, and it is the whole difference between a
    // thumb and a finger. Both previous paths put it somewhere else and were
    // measured doing so: along the analyzer's branch chain the four nodes
    // landed at 0.90–0.96 of the way to the tip (the chain is a 4–10% tip
    // FRAGMENT on this corpus, so the whole armature compressed into it), and
    // the span fallback put it on the shared FINGER knuckle arc — 0.91 on
    // gilda, whose thumb is short relative to the longest finger.
    //
    // So the thumb's span is wrist→tip and its ladder is its own (45:32:23).
    // The chain is not a ladder any more; §5's cutover keeps it as a PATH,
    // which arrives when the medial seating lands (T2) — this is the landmark
    // half, and it is the half that was making the joint NAMES a lie: calling a
    // point at 90% of the digit `cmc` while frame authoring believed it
    // (Codex P1, #96).
    if (pin.digit === 'Thumb') {
      // THE CMC SEATS AT THE ATTACH (field 2026-08-14, Eric): the chain hangs
      // from the hand JOINT, and a CMC at the wrist-CUT centre gave the thumb
      // two bones to the knuckle — a backward connector, then a metacarpal
      // sweeping the whole palm and stealing the middle finger's flesh. One
      // clean bone, attach → MCP, is both Allen's read and the anatomy (the
      // metacarpal runs the radial edge, not the palm's middle).
      if (pin.chain === undefined || pin.chain.length < 2) {
        // Still worth saying: a thumb the volume never separated has no
        // evidence behind it beyond the person's pin (D2's marker, kept).
        markers.push({
          code: 'thumb-span-fallback',
          detail: { note: 'no separating chain for the thumb — placed along wrist→tip like a finger' },
        });
      }
      const ladder = digitAnatomy('Thumb').fractions;
      // ── A PINNED KNUCKLE PINS THE THUMB'S MCP (ADR-024 §2.2, D2) ─────────
      // Two MEASURED points outrank any inference, so this construction stays:
      // the CMC at the hand attach (one clean bone to the knuckle — Eric's
      // fix), the MCP at the click's station, the rest on the straight chord.
      if (pin.knuckle !== undefined) {
        const cmcAt = options.handAttach ?? wrist;
        const spanLength = dist(cmcAt, pin.tip);
        if (!(spanLength > 1e-9)) {
          markers.push({ code: 'digit-degenerate-span', detail: { digit: pin.digit, source: pin.sourceId ?? 'unnamed' } });
          return null;
        }
        const distal = (f: number): number => (f - ladder[1]!) / (1 - ladder[1]!);
        const kTube = medialPath(options.grid, pin.knuckle, pin.tip);
        if (kTube === null) {
          markers.push({
            code: 'knuckle-pin-unseated',
            detail: { digit: pin.digit, source: pin.sourceId ?? 'unnamed' },
          });
          const mcp = pin.knuckle;
          const chordPos = [cmcAt, mcp, lerp(mcp, pin.tip, distal(ladder[2]!)), pin.tip];
          return digitOf(chordPos, pin, spanLength, 'phi-prior', null);
        }
        const mcp = kTube.stations[0]!.at; // the click names the station; the centroid names the interior
        const curl = curlOf(kTube, 0);
        const chordPos = [cmcAt, mcp, lerp(mcp, pin.tip, distal(ladder[2]!)), pin.tip];
        const escape = Math.max(
          chordEscapeOf(options.grid, cmcAt, mcp),
          chordEscapeOf(options.grid, mcp, pin.tip),
        );
        if (escape <= chordEscape) return digitOf(chordPos, pin, spanLength, 'chord-validated', curl);
        reportEscape(pin, escape, curl);
        return digitOf(chordPos, pin, spanLength, 'phi-prior', curl);
      }
      // ── THE THUMB IS attach → tip, STRAIGHT, ALWAYS (ADR-024 §13.2) ────────
      // The chain-φ thumb (the "field-good" construction, restored in §13.1)
      // was swept across fourteen `_new_v3` specimens the next morning: on
      // EVERY one the analyzer's thumb chain is a 3–13-voxel tip fragment, so
      // φ along it compressed the whole armature into the last 10–30% of the
      // digit (Karl `[0.77 0.90 0.97 1.00]`, Brenda `[0.93 0.97 0.99 1.00]`) —
      // and on four hands (Trey L/R, Wyatt L, Ian R) the fragment runs
      // BACKWARD (`[1.01 1.00 1.00 1.00]`), stacking every joint on the tip
      // with the base beyond it, so the authored primary pointed the wrong way
      // and the thumb moved against its animation. ADR-023 §1.5 had measured
      // exactly this on gilda/kobold; re-opening it was the wrong call.
      //
      // So the chain is NEVER a ladder. The thumb's CMC seats at the hand
      // attach (the point the grafted chain hangs from — Eric's one-clean-bone
      // fix) and its ladder is the anthropometric 45:32:23 (D2) laid on the
      // straight attach→tip chord: base→tip by construction, the whole digit
      // filled, the frame primary forward. The palm-sweeping metacarpal this
      // re-introduces is a WEIGHTS problem — a bind concern, taken up there —
      // not a placement one; a wrong-direction stub is not an alternative.
      const cmcAt = options.handAttach ?? wrist;
      const spanLength = dist(cmcAt, pin.tip);
      if (!(spanLength > 1e-9)) {
        markers.push({ code: 'digit-degenerate-span', detail: { digit: pin.digit, source: pin.sourceId ?? 'unnamed' } });
        return null;
      }
      const chord = ladder.map((f) => lerp(cmcAt, pin.tip, f));
      const tube = tubes.get('Thumb') ?? null;
      if (tube === null) return digitOf(chord, pin, spanLength, 'phi-prior', null);
      const curl = curlOf(tube, 0);
      const escape = chordEscapeOf(options.grid, cmcAt, pin.tip);
      if (escape <= chordEscape) return digitOf(chord, pin, spanLength, 'chord-validated', curl);
      reportEscape(pin, escape, curl);
      return digitOf(chord, pin, spanLength, 'phi-prior', curl);
    }
    const tip = pin.tip;
    const spanLength = dist(wrist, tip);
    if (!(spanLength > 1e-9)) {
      markers.push({ code: 'digit-degenerate-span', detail: { digit: pin.digit, source: pin.sourceId ?? 'unnamed' } });
      return null;
    }
    const ladder = digitAnatomy(pin.digit).fractions;
    const tube = tubes.get(pin.digit) ?? null;
    const shortOfPalm = (available: number): boolean => !(available - knuckleArc > 1e-9);
    const sayShort = (available: number): void => {
      // The tip sits INSIDE the shared knuckle arc — a digit shorter than the
      // palm. Place by its own fraction so it still articulates, and say so.
      markers.push({
        code: 'finger-shorter-than-palm',
        detail: {
          digit: pin.digit,
          source: pin.sourceId ?? 'unnamed',
          wristToTip: Number(available.toFixed(4)),
          knuckleArc: Number(knuckleArc.toFixed(4)),
        },
      });
    };
    // When a chain IS available, its own axis is the digit's local direction;
    // disagreement with the span means the digit likely curls in rest pose and
    // the straight span is suspect — flag for a human, place anyway.
    if (pin.chain !== undefined && pin.chain.length >= 2) {
      const smooth = smoothChain(pin.chain);
      const frag = sub(smooth[smooth.length - 1]!, smooth[0]!);
      const span = sub(tip, wrist);
      if (len(frag) > 1e-9 && len(span) > 1e-9) {
        const cos = Math.min(1, Math.max(-1, dot(frag, span) / (len(frag) * len(span))));
        const degrees = (Math.acos(cos) * 180) / Math.PI;
        if (degrees > AXIS_DIVERGENCE_DEGREES) {
          markers.push({
            code: 'digit-axis-divergent',
            detail: { digit: pin.digit, source: pin.sourceId ?? 'unnamed', degrees: Number(degrees.toFixed(1)) },
          });
        }
      }
    }
    // ── A PINNED KNUCKLE REPLACES THE ROW INFERENCE (ADR-024 §2.2, D2) ─────
    // The whole ADR-023 §12.1 failure — five knuckles collapsing onto the
    // palm's medial axis, routes leaking through neighbouring fingers — came
    // from every path sharing the wrist. A pinned knuckle removes the shared
    // segment: the route is MCP→tip, inside this digit's own tube, and the
    // MCP itself is MEASURED (§1.2 named the inferred one the worst offender,
    // 11 voxels out). The switch still runs, on the far better chord the pin
    // makes possible — most pinned digits flip back to the biometric build.
    if (pin.knuckle !== undefined) return buildFromKnuckle(pin, pin.knuckle, 'pinned');

    // ── THE CHORD CONSTRUCTION — one door for both chord outcomes (ADR-024) ─
    // The biometric build the field called "right about direction": the MCP on
    // the shared knuckle arc along wrist→tip, PIP/DIP at φ fractions of the
    // MCP→tip chord — each digit aiming at its own tip from the first voxel,
    // which is where the splay lives. Reached two ways, and the provenance
    // says which claim is being made: `chord-validated` when the tube was
    // walked and measured straight, `phi-prior` when no tube could be walked
    // and nobody checked (§7).
    const fingerChord = (provenance: 'phi-prior' | 'chord-validated', measuredCurl: number | null): PlacedDigit => {
      if (shortOfPalm(spanLength)) sayShort(spanLength);
      const mcp = shortOfPalm(spanLength)
        ? lerp(wrist, tip, palmFraction)
        : lerp(wrist, tip, knuckleArc / spanLength);
      return digitOf(ladder.map((f) => lerp(mcp, tip, f)), pin, dist(mcp, tip), provenance, measuredCurl, ladder);
    };
    if (tube === null) return fingerChord('phi-prior', null);

    // ── THE VERDICT (ADR-024 D1, amended TWICE — the field's final ruling) ──
    // The interior ladder is ALWAYS the chord (Allen, 2026-08-14 evening: the
    // tube's interim points made "swollen, deformed, elongated fingers"; the
    // straight biometric line from knuckle to tip "looks much better"). The
    // tube is an INSTRUMENT now, never a placer: its curl feeds the hand
    // classifier, and a chord the volume cannot endorse is REPORTED
    // (`digit-chord-escapes`) with `phi-prior` provenance rather than re-laid.
    const total = tube.length;
    const mcpProposal = shortOfPalm(total) ? palmFraction * total : knuckleArc;
    const curl = curlOf(tube, mcpProposal);
    const chordMcp = shortOfPalm(spanLength)
      ? lerp(wrist, tip, palmFraction)
      : lerp(wrist, tip, knuckleArc / spanLength);
    const escape = chordEscapeOf(options.grid, chordMcp, tip);
    if (escape <= chordEscape) return fingerChord('chord-validated', curl);
    reportEscape(pin, escape, curl);
    return fingerChord('phi-prior', curl);
  };

  const placed: PlacedDigit[] = [];
  for (const pin of ordered) {
    const digit = place(pin);
    if (digit !== null) placed.push(digit);
  }

  // ── PINNED KNUCKLES ARE EVIDENCE ABOUT THE UNPINNED ONES (ADR-024 §2.3) ──
  // Two pinned knuckles define the row's LINE and its SPACING, so from two the
  // row prior for the remaining fingers stops being `palmFraction × longest`
  // and starts being measured: each unpinned MCP is moved to its predicted
  // slot ALONG the row axis only — the anisotropic rule: across-the-row is the
  // axis a digit's identity decides (and the axis that broke, ADR-023 §12.1);
  // along-the-digit and through-the-palm stay the digit's own construction's.
  // The digit is then rebuilt from that virtual knuckle through the same door
  // a clicked one uses, so the escape switch re-decides chord-vs-path from the
  // corrected base. D4: everything is reported, nothing surfaces.
  const knuckledOf = new Set(
    ordered.filter((p) => p.digit !== 'Thumb' && p.knuckle !== undefined).map((p) => p.digit),
  );
  const rowFingers = placed.filter((d) => d.name !== 'Thumb');
  const anchors = rowFingers.filter((d) => knuckledOf.has(d.name));
  const learners = rowFingers.filter((d) => !knuckledOf.has(d.name));
  if (anchors.length >= 2 && learners.length > 0) {
    const idxOf = (n: DigitName): number => DIGIT_ORDER.indexOf(n);
    const anchorPts = anchors.map((d) => d.joints[0]!.position);
    const rowMean = scale(anchorPts.reduce(add, { x: 0, y: 0, z: 0 }), 1 / anchorPts.length);
    const rowAxis = dominantAxis(anchorPts);
    // Regression of the along-row coordinate on the DIGIT INDEX: the slope IS
    // the spacing per digit step, which is what colinearity alone can never
    // see — five knuckles collapsed onto one point are perfectly colinear and
    // measure spacing ~0 here, in which case the row has nothing to teach.
    const samples = anchors.map((d) => ({
      i: idxOf(d.name),
      t: dot(sub(d.joints[0]!.position, rowMean), rowAxis),
    }));
    const iMean = samples.reduce((s, p) => s + p.i, 0) / samples.length;
    const tMean = samples.reduce((s, p) => s + p.t, 0) / samples.length;
    const denom = samples.reduce((s, p) => s + (p.i - iMean) ** 2, 0);
    const spacing = denom > 1e-9 ? samples.reduce((s, p) => s + (p.i - iMean) * (p.t - tMean), 0) / denom : 0;
    markers.push({
      code: 'knuckle-row-measured',
      detail: { anchors: anchors.length, spacingVoxels: Number(Math.abs(spacing).toFixed(4)) },
    });
    if (Math.abs(spacing) > 1e-9) {
      for (const learner of learners) {
        const pin = ordered.find((p) => p.digit === learner.name)!;
        const mcp = learner.joints[0]!.position;
        const tPredicted = tMean + spacing * (idxOf(learner.name) - iMean);
        const moved = tPredicted - dot(sub(mcp, rowMean), rowAxis);
        const virtual = add(mcp, scale(rowAxis, moved));
        // The escape verdict belongs to the placement that STANDS (Codex P2,
        // #97): the first pass may have reported this digit's wrist-based
        // chord escaping; the rebuild re-decides from the corrected base, so
        // the old entry goes before it runs and comes back only if the rebuild
        // is refused and the first-pass digit is kept.
        const wasEscaped = chordEscaped.delete(learner.name);
        // …and the MARKER it wrote goes with it (Codex P2, #97 round three):
        // `digit-chord-escapes` propagates into the graft report, so a retracted
        // verdict must not leave its report line behind — nor be written twice
        // if the rebuilt chord escapes as well. Held aside; restored only when
        // the rebuild is refused and the first-pass digit is what stands.
        const heldMarkers = markers.filter((m) => m.code === 'digit-chord-escapes' && m.detail?.['digit'] === learner.name);
        for (const m of heldMarkers) markers.splice(markers.indexOf(m), 1);
        const rebuilt = buildFromKnuckle(pin, virtual, 'row-inferred');
        if (rebuilt === null) {
          if (wasEscaped) chordEscaped.add(learner.name);
          markers.push(...heldMarkers);
        }
        if (rebuilt !== null) {
          markers.push({
            code: 'knuckle-row-inferred',
            detail: { digit: learner.name, movedVoxels: Number(Math.abs(moved).toFixed(4)) },
          });
          placed[placed.indexOf(learner)] = rebuilt;
        }
      }
    }
  }

  // THE SPACING DIAGNOSTIC — colinearity's missing sibling (ADR-024 §2.3):
  // `rowResiduals` measures whether the knuckles lie on a line, and five
  // knuckles collapsed onto one point score ZERO residual, so the ADR-023
  // §12.1 failure was invisible to the existing diagnostic by construction.
  // An adjacent gap under half the mean gap is the collapse signature, said
  // in the report (D4).
  const finalFingers = placed.filter((d) => d.name !== 'Thumb');
  if (finalFingers.length >= 3) {
    const gaps = finalFingers.slice(1).map((d, k) => dist(finalFingers[k]!.joints[0]!.position, d.joints[0]!.position));
    const meanGap = gaps.reduce((s, g) => s + g, 0) / gaps.length;
    if (meanGap > 1e-9 && gaps.some((g) => g < 0.5 * meanGap)) {
      markers.push({
        code: 'row-spacing-collapsed',
        detail: { gapsVoxels: gaps.map((g) => Number(g.toFixed(3))), meanVoxels: Number(meanGap.toFixed(3)) },
      });
    }
  }

  // --- knuckle-row residuals (fingers only), optional soft snap ---
  const fingerDigits = placed.filter((d) => d.name !== 'Thumb');
  const rowResiduals = { mcp: 0, pip: 0, dip: 0 };
  (['mcp', 'pip', 'dip'] as const).forEach((row, rowIdx) => {
    const rowPts = fingerDigits.map((d) => d.joints[rowIdx]!.position);
    rowResiduals[row] = Number(lineResidual(rowPts).toFixed(4));
    if (alpha > 0 && rowPts.length >= 2) {
      const mean = scale(rowPts.reduce(add, { x: 0, y: 0, z: 0 }), 1 / rowPts.length);
      const axis = dominantAxis(rowPts);
      fingerDigits.forEach((d, di) => {
        const p = rowPts[di]!;
        const proj = add(mean, scale(axis, dot(sub(p, mean), axis)));
        (d.joints[rowIdx] as { position: VoxelPoint }).position = lerp(p, proj, alpha);
      });
    }
  });

  // ── THE HAND'S CLASS, measured off this placement's own work (§2.4) ──────
  // The curl axis is the escape switch's verdict per digit; the adduction
  // axis is whether adjacent tubes touch at mid-digit (station at 0.75 of the
  // wrist→tip walk — past the knuckle, in the digit proper). Reported always,
  // gating nothing: a cupped hand grafts with more help offered, not less.
  const handClass = classifyHand(
    placed.map((d) => {
      const tube = tubes.get(d.name) ?? null;
      if (tube === null || tube.stations.length === 0) {
        return { name: d.name, escaped: chordEscaped.has(d.name), mid: null, midRadius: null };
      }
      const targetArc = 0.75 * tube.length;
      let station = tube.stations[0]!;
      for (const s of tube.stations) {
        if (Math.abs(s.arclength - targetArc) < Math.abs(station.arclength - targetArc)) station = s;
      }
      return { name: d.name, escaped: chordEscaped.has(d.name), mid: station.at, midRadius: station.radius };
    }),
  );
  markers.push({
    code: 'hand-class',
    detail: {
      klass: handClass.klass,
      escapedDigits: handClass.escapedDigits,
      adductedPairs: handClass.adductedPairs.map((p) => p.join('+')).join(', '),
    },
  });

  return { side: options.side, digits: placed, rowResiduals, handClass, markers };
}
